import { FC } from 'react';
import {
  Stack,
  Checkbox,
  TextInput,
  Text,
  Group,
  Button,
  Paper,
  Divider,
  Badge,
  ActionIcon,
} from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PedigreeGeneticData, PedigreeEvaluation } from '../types';

// -----------------------------------------------------------------------------
interface GeneticDataFormProps {
  geneticData: PedigreeGeneticData;
  onChange: (geneticData: PedigreeGeneticData) => void;
}

// -----------------------------------------------------------------------------
export const GeneticDataForm: FC<GeneticDataFormProps> = ({
  geneticData,
  onChange,
}) => {
  const { t } = useTranslation('pedigree');
  // ---------------------------------------------------------------------------
  const handleBasicChange = (field: keyof PedigreeGeneticData, value: any) => {
    onChange({
      ...geneticData,
      [field]: value,
    });
  };

  // ---------------------------------------------------------------------------
  const handleAddEvaluation = () => {
    const evaluations = geneticData.evaluations || [];
    const nextNumber = evaluations.length + 1;
    const newEvaluation: PedigreeEvaluation = {
      id: Date.now().toString(),
      label: `E${nextNumber}`,
      description: '',
    };

    onChange({
      ...geneticData,
      evaluations: [...evaluations, newEvaluation],
    });
  };
  // ---------------------------------------------------------------------------
  const handleRemoveEvaluation = (evaluationId: string) => {
    const evaluations = geneticData.evaluations || [];
    const updatedEvaluations = evaluations.filter((e) => e.id !== evaluationId);

    // Renumber the remaining evaluations
    const renumberedEvaluations = updatedEvaluations.map(
      (evaluation, index) => ({
        ...evaluation,
        label: `E${index + 1}`,
      })
    );

    onChange({
      ...geneticData,
      evaluations: renumberedEvaluations,
    });
  };

  // ---------------------------------------------------------------------------
  const handleEvaluationChange = (
    evaluationId: string,
    field: keyof PedigreeEvaluation,
    value: any
  ) => {
    const evaluations = geneticData.evaluations || [];
    const updatedEvaluations = evaluations.map((evaluation) =>
      evaluation.id === evaluationId
        ? { ...evaluation, [field]: value }
        : evaluation
    );

    onChange({
      ...geneticData,
      evaluations: updatedEvaluations,
    });
  };

  // ---------------------------------------------------------------------------
  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {t('geneticDataForm.description')}
      </Text>
      {/* Basic Genetic Markers */}
      <Stack gap="xs">
        <Text fw={500} size="sm">
          {t('geneticDataForm.geneticStatusMarkers')}
        </Text>{' '}
        <Checkbox
          label={t('geneticDataForm.documentedEvaluation.label')}
          description={t('geneticDataForm.documentedEvaluation.description')}
          checked={geneticData.hasDocumentedEvaluation || false}
          onChange={(e) =>
            handleBasicChange(
              'hasDocumentedEvaluation',
              e.currentTarget.checked
            )
          }
        />
        <Checkbox
          label={t('geneticDataForm.carrier.label')}
          description={t('geneticDataForm.carrier.description')}
          checked={geneticData.isCarrier || false}
          onChange={(e) =>
            handleBasicChange('isCarrier', e.currentTarget.checked)
          }
        />
        <Checkbox
          label={t('geneticDataForm.asymptomaticCarrier.label')}
          description={t('geneticDataForm.asymptomaticCarrier.description')}
          checked={geneticData.isAsymptomaticCarrier || false}
          onChange={(e) =>
            handleBasicChange('isAsymptomaticCarrier', e.currentTarget.checked)
          }
        />
      </Stack>
      <Divider />
      {/* Evaluations Section */}{' '}
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={500} size="sm">
            {t('geneticDataForm.evaluations.title')}
          </Text>
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddEvaluation}
          >
            {t('geneticDataForm.evaluations.addButton')}
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          {t('geneticDataForm.evaluations.description')}
        </Text>

        {(geneticData.evaluations || []).map((evaluation) => (
          <Paper key={evaluation.id} p="sm" withBorder>
            <Stack gap="xs">
              <Group justify="space-between" align="flex-start">
                <Badge variant="filled" size="sm">
                  {evaluation.label}
                </Badge>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={() => handleRemoveEvaluation(evaluation.id)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>{' '}
              <TextInput
                label={t('geneticDataForm.evaluations.descriptionLabel')}
                placeholder={t(
                  'geneticDataForm.evaluations.descriptionPlaceholder'
                )}
                value={evaluation.description}
                onChange={(e) =>
                  handleEvaluationChange(
                    evaluation.id,
                    'description',
                    e.target.value
                  )
                }
                size="xs"
              />
              <Group grow>
                <Checkbox
                  label={t('geneticDataForm.evaluations.positive')}
                  checked={evaluation.isPositive || false}
                  onChange={(e) =>
                    handleEvaluationChange(
                      evaluation.id,
                      'isPositive',
                      e.currentTarget.checked
                    )
                  }
                  size="xs"
                />
                <Checkbox
                  label={t('geneticDataForm.evaluations.uninformative')}
                  checked={evaluation.isUninformative || false}
                  onChange={(e) =>
                    handleEvaluationChange(
                      evaluation.id,
                      'isUninformative',
                      e.currentTarget.checked
                    )
                  }
                  size="xs"
                />
              </Group>
              <TextInput
                label={t('geneticDataForm.evaluations.testResultsLabel')}
                placeholder={t(
                  'geneticDataForm.evaluations.testResultsPlaceholder'
                )}
                value={evaluation.testResults || ''}
                onChange={(e) =>
                  handleEvaluationChange(
                    evaluation.id,
                    'testResults',
                    e.target.value
                  )
                }
                size="xs"
              />
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
};
